import { GetConvertRate } from './context/BodyTypes';

/**
 * A currency is always worth exactly one of itself, so no `exchangerates` row is ever stored for
 * base === quote (the daily Bank Leumi job skips it explicitly). Every lookup therefore has to
 * resolve the identity pair itself — otherwise the local currency reads as "no rate", which the
 * grids render as 0.00000 and conversions treat as missing.
 */
export const isSameCurrency = (base: unknown, quote: unknown): boolean => {
    const baseId = base != null && typeof base === 'object' ? (base as any)._id : base;
    const quoteId = quote != null && typeof quote === 'object' ? (quote as any)._id : quote;
    return baseId != null && quoteId != null && String(baseId) === String(quoteId);
};

/** The rate of a currency against itself. */
export const IDENTITY_RATE = { exchangeRateSell: 1, exchangeRateBuy: 1 };

/**
 * Unwrap a `getConvertRate` result to a plain number.
 *
 * The result has three shapes: the {exchangeRateSell, exchangeRateBuy} object,
 * the bare number 1 when an id is missing, and null when no rate exists. Once
 * IDENTITY_RATE made the same-currency case return an OBJECT rather than null,
 * every caller written as `getConvertRate(...) || 1` stopped falling through —
 * an object is truthy — and wrote it into a numeric column, which Postgres
 * rejects with `invalid input syntax for type numeric`. Use this instead of
 * reading `.exchangeRateSell` by hand, so the number case is not lost either.
 */
export const toRateSell = (rate: unknown, fallback = 0): number => {
    if (rate == null) return fallback;
    if (typeof rate === 'number') return Number.isFinite(rate) ? rate : fallback;
    const sell = (rate as { exchangeRateSell?: unknown }).exchangeRateSell;
    return typeof sell === 'number' && Number.isFinite(sell) ? sell : fallback;
};

export const getConvertRateByExchangeRate: GetConvertRate = (base, quote, exchangeRateData) => {
    const baseCurrencyId = typeof base !== 'object' ? base : base?._id;
    const quoteCurrencyId = typeof quote !== 'object' ? quote : quote?._id;
    if (!baseCurrencyId || !quoteCurrencyId) {
        return 1;
    }
    if (isSameCurrency(baseCurrencyId, quoteCurrencyId)) {
        return { ...IDENTITY_RATE };
    }
    const exchangeRate = exchangeRateData?.find(
        (rate) =>
            rate.base._id.toString() === baseCurrencyId.toString() &&
            rate.quote._id.toString() === quoteCurrencyId.toString()
    );
    if (!exchangeRate) {
        return null;
    }
    const { exchangeRateSell, exchangeRateBuy } = exchangeRate;
    return { exchangeRateSell, exchangeRateBuy };
};
